import type { BusinessInvoice } from '@pulsify/shared/dto'
import { Download, Receipt, Wallet } from 'lucide-react'
import { useCreatePortalSession, useInvoices } from '~/entities/subscription/model/use-stripe'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'

const STATUS_LABEL: Record<BusinessInvoice['status'], () => string> = {
  paid: m.invoiceStatusPaid,
  open: m.invoiceStatusOpen,
  void: m.invoiceStatusVoid,
  uncollectible: m.invoiceStatusUncollectible,
}

function formatInvoiceDate(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), {
    timeZone: 'Europe/Vienna',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso))
}

function formatInvoiceTotal(invoice: BusinessInvoice): string {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: invoice.currency,
  }).format(invoice.totalCents / 100)
}

function InvoiceRow({ invoice }: { invoice: BusinessInvoice }) {
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 p-4">
      <div className="min-w-40 flex-1">
        <p className="font-medium text-sm tabular-nums">{formatInvoiceDate(invoice.createdAt)}</p>
        <p className="text-muted-foreground text-xs">{invoice.number}</p>
      </div>
      <Badge variant={invoice.status === 'paid' ? 'secondary' : 'outline'}>
        {STATUS_LABEL[invoice.status]()}
      </Badge>
      <p className="w-24 text-right font-medium text-sm tabular-nums">
        {formatInvoiceTotal(invoice)}
      </p>
      {invoice.pdfUrl ? (
        <Button asChild variant="outline" size="sm">
          <a href={invoice.pdfUrl} target="_blank" rel="noreferrer">
            <Download />
            {m.invoiceDownload()}
          </a>
        </Button>
      ) : null}
    </li>
  )
}

/** Every invoice of the business with its PDF, and the way to change how it pays. */
export function InvoicesPanel({
  businessId,
  portalProfileId,
}: {
  businessId: string
  portalProfileId?: string
}) {
  const { data: invoices, isLoading, isError } = useInvoices(businessId)
  const portal = useCreatePortalSession(businessId, portalProfileId)

  if (isLoading) return <Skeleton className="h-48 w-full rounded-xl" />

  if (isError || !invoices?.length) {
    return (
      <Card className="flex flex-col items-center gap-3 p-10 text-center">
        <Receipt className="size-6 text-muted-foreground" />
        <p className="text-muted-foreground text-sm">
          {isError ? m.invoicesLoadFailed() : m.invoicesEmpty()}
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      <Card className="p-0">
        <ul className="divide-y">
          {invoices.map((invoice) => (
            <InvoiceRow key={invoice.id} invoice={invoice} />
          ))}
        </ul>
      </Card>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        onClick={() => portal.mutate()}
        disabled={portal.isPending}
      >
        <Wallet />
        {m.managePaymentMethod()}
      </Button>
    </div>
  )
}
